import _ from 'lodash';

import * as api from '../../../../api';

import { Field, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';
import {
  TextField,
  Typography,
  RadioButton,
  VStack,
} from '@vivaahealth/design-system';

import LabeledInput from '../../../LabeledInput';

import {
  EditEmergencyContactValues,
  AddEmergencyContactValues,
} from './schema';
import { getOptionsFromRecord } from '../../../utils';
import { SelectInput } from '../../../SelectInput';
import { formatPhone, normalize } from '../../../../utils/phoneNumber';

const TEXT_FIELD_DATA: {
  fieldKey: keyof EditEmergencyContactValues;
}[] = [{ fieldKey: 'firstName' }, { fieldKey: 'lastName' }];

interface Props {
  formikProps:
    | FormikProps<EditEmergencyContactValues>
    | FormikProps<AddEmergencyContactValues>;
  emergencyContactRelationships: api.Patient.EmergencyContactRelationship[];
}

export const EmergencyContactFields = ({
  formikProps,
  emergencyContactRelationships,
}: Props) => {
  const { t } = useTranslation('common');

  const selectOptions = getOptionsFromRecord(
    emergencyContactRelationships.length
      ? _.pick(api.Previsit.relationshipMap, emergencyContactRelationships)
      : api.Previsit.relationshipMap,
  );

  const phoneDetails = {
    key: formikProps.values.isMobile ? 'cellPhoneNumber' : 'homePhoneNumber',
    label: formikProps.values.isMobile ? t('mobilePhone') : t('homePhone'),
    value: formikProps.values.isMobile
      ? formatPhone(formikProps.values.cellPhoneNumber || '')
      : formatPhone(formikProps.values.homePhoneNumber || ''),
  };

  const handlePhoneInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === t('mobilePhone')) {
      formikProps.setFieldValue('isMobile', true);
      formikProps.setFieldValue('isHome', false);
    } else {
      formikProps.setFieldValue('isMobile', false);
      formikProps.setFieldValue('isHome', true);
    }
  };

  return (
    <VStack gap={20}>
      {TEXT_FIELD_DATA.map(({ fieldKey }) => (
        <Field
          as={TextField}
          key={fieldKey}
          label={t(fieldKey)}
          name={fieldKey}
          error={formikProps.touched[fieldKey] && formikProps.errors[fieldKey]}
        />
      ))}
      <VStack gap={12}>
        <Typography variant="title-large" bold>
          {t('phoneType')}
        </Typography>
        <RadioButton
          label={t('mobilePhone')}
          name="phoneInfo"
          value={t('mobilePhone')}
          checked={formikProps.values.isMobile}
          onChange={handlePhoneInfoChange}
        />
        <RadioButton
          label={t('homePhone')}
          name="phoneInfo"
          value={t('homePhone')}
          checked={formikProps.values.isHome}
          onChange={handlePhoneInfoChange}
        />
        {(formikProps.touched.isMobile || formikProps.touched.isHome) &&
          !formikProps.values.isMobile &&
          !formikProps.values.isHome && (
            <Typography
              as="span"
              color="danger"
              variant={'paragraph-medium'}
              id={'error'}
            >
              {t('emergencyContactModalRequiredFieldError', {
                ns: 'emergencyContacts',
                fieldName: 'Primary phone',
              })}
            </Typography>
          )}
      </VStack>
      {(formikProps.values.isMobile || formikProps.values.isHome) && (
        <Field
          as={TextField}
          key={phoneDetails.key}
          label={phoneDetails.label}
          name={phoneDetails.key}
          error={
            (formikProps.touched as any)[phoneDetails.key] &&
            (formikProps.errors as any)[phoneDetails.key]
          }
          value={phoneDetails.value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const phoneNumber: string = e.target.value;
            formikProps.setFieldValue(phoneDetails.key, normalize(phoneNumber));
          }}
        />
      )}
      <VStack gap={8}>
        <LabeledInput
          label={t('relationshipToPatient')}
          htmlFor="relationship-options"
        />
        <SelectInput
          id="relationship-options"
          options={selectOptions}
          value={formikProps.values.emergencyContactRelationshipToPatient || ''}
          onChange={(newRelationship: string) => {
            formikProps.setFieldValue(
              'emergencyContactRelationshipToPatient',
              newRelationship,
            );
          }}
        />
        {!!formikProps.touched.emergencyContactRelationshipToPatient && (
          <Typography
            as="span"
            color="danger"
            variant={'paragraph-medium'}
            id={'error'}
          >
            {formikProps.errors.emergencyContactRelationshipToPatient}
          </Typography>
        )}
      </VStack>
    </VStack>
  );
};
